// Palworld OFFICIAL CARD GAME booster-pack simulation engine.
//
// Pack specification (Booster Pack "Dawn of Palpagos", EBP01, on sale July 30 2026):
//   • 100 base card types (RR / R / U / C) + 61 parallel types
//   • 7 cards per pack, 12 packs (84 cards) per booster box
//   • Slot layout: 4 Common, 2 Uncommon, 1 "hit" slot (R or better, parallels included)
// Odds below are the published-style box math also used by /tcg-tools, normalised
// across the hit slot so a pack always resolves to a real printed card number.

import { OFFICIAL_CARDS, type OfficialCard, type OfficialRarity } from "./tcg-cards";

export const CARDS_PER_PACK = 7;
export const PACKS_PER_BOX = 12;
export const DAILY_FREE_PACKS = 12;

/** Hit-slot distribution (sums to 1). Common/Uncommon slots are fixed. */
export const HIT_SLOT_ODDS: { rarity: OfficialRarity; weight: number }[] = [
  { rarity: "R", weight: 0.608 },
  { rarity: "RR", weight: 0.2 },
  { rarity: "SR", weight: 0.1 },
  { rarity: "SP", weight: 0.05 },
  { rarity: "OSR", weight: 0.025 },
  { rarity: "SSP", weight: 0.012 },
  { rarity: "SSS", weight: 0.005 },
];

export const RARITY_GLOW: Record<string, string> = {
  C: "#94a3b8",
  U: "#22d3ee",
  R: "#3b82f6",
  RR: "#8b5cf6",
  SR: "#f59e0b",
  SP: "#f97316",
  OSR: "#ef4444",
  SSP: "#ec4899",
  SSS: "#fbbf24",
};

/** Collector value score used for the daily leaderboard (rarity + grade). */
export const RARITY_SCORE: Record<string, number> = {
  C: 1, U: 3, R: 10, RR: 30, SR: 60, SP: 90, OSR: 160, SSP: 320, SSS: 750,
};

const BOOSTER = OFFICIAL_CARDS.filter((c) => c.set === "BP01" && !!c.image);
const byRarity = (r: OfficialRarity) => BOOSTER.filter((c) => c.rarity === r);

const POOLS: Record<string, OfficialCard[]> = {
  C: byRarity("C"),
  U: byRarity("U"),
  R: byRarity("R"),
  RR: byRarity("RR"),
  SR: byRarity("SR"),
  SP: byRarity("SP"),
  OSR: byRarity("OSR"),
  SSP: byRarity("SSP"),
  SSS: byRarity("SSS"),
};

function pick<T>(arr: T[]): T | undefined {
  if (arr.length === 0) return undefined;
  return arr[Math.floor(Math.random() * arr.length)];
}

function pickRarityCard(rarity: OfficialRarity, fallbacks: OfficialRarity[]): OfficialCard {
  const chain = [rarity, ...fallbacks];
  for (const r of chain) {
    const hit = pick(POOLS[r] ?? []);
    if (hit) return hit;
  }
  return BOOSTER[0];
}

function rollHitRarity(): OfficialRarity {
  const roll = Math.random();
  let acc = 0;
  for (const o of HIT_SLOT_ODDS) {
    acc += o.weight;
    if (roll <= acc) return o.rarity;
  }
  return "R";
}

// ---------------------------------------------------------------------------
// Grading engine — mirrors how PSA / BGS / CGC score a raw card.
// ---------------------------------------------------------------------------

export interface CardGrade {
  /** Overall numeric grade, 1.0 – 10.0 in half steps. */
  grade: number;
  label: string;
  centering: number;
  corners: number;
  edges: number;
  surface: number;
  /** Human-readable defects a grader would note on the label. */
  defects: string[];
  /** True only for a flawless 10 across every sub-grade. */
  pristine: boolean;
}

export const GRADE_LABELS: { min: number; label: string }[] = [
  { min: 10, label: "Gem Mint 10" },
  { min: 9.5, label: "Mint+ 9.5" },
  { min: 9, label: "Mint 9" },
  { min: 8, label: "Near Mint–Mint 8" },
  { min: 7, label: "Near Mint 7" },
  { min: 6, label: "Excellent–Mint 6" },
  { min: 5, label: "Excellent 5" },
  { min: 4, label: "Very Good–Excellent 4" },
  { min: 3, label: "Very Good 3" },
  { min: 2, label: "Good 2" },
  { min: 1, label: "Poor 1" },
];

function labelFor(grade: number) {
  return GRADE_LABELS.find((g) => grade >= g.min)?.label ?? "Poor 1";
}

const DEFECTS: Record<keyof Omit<CardGrade, "grade" | "label" | "defects" | "pristine">, string[]> = {
  centering: ["Off-centre left/right border", "Top border thinner than bottom", "Slight diamond centring"],
  corners: ["Soft top-left corner", "Minor corner fuzzing", "Blunted bottom-right corner", "Whitening on two corners"],
  edges: ["Edge whitening along the top", "Chipping on the right edge", "Rough factory cut", "Nick on the bottom edge"],
  surface: ["Print line through the art", "Surface scratch in the foil", "Light indentation", "Loss of gloss", "Roller mark", "Fingerprint on the holo"],
};

/**
 * Roll a grade the way a real submission comes back: perfect 10s are rare
 * (~1.2% of pulls), most raw cards land Mint 9 to NM–Mint 8, and lower grades
 * carry listed defects.
 */
export function rollGrade(): CardGrade {
  const roll = Math.random();
  let base: number;
  if (roll < 0.012) base = 10;
  else if (roll < 0.09) base = 9.5;
  else if (roll < 0.32) base = 9;
  else if (roll < 0.64) base = 8;
  else if (roll < 0.84) base = 7;
  else if (roll < 0.95) base = 6;
  else base = 5;


  const jitter = () => {
    if (base === 10) return 10;
    const d = Math.random();
    const step = d < 0.5 ? 0 : d < 0.85 ? 0.5 : 1;
    return Math.max(1, Math.min(10, base + (Math.random() < 0.5 ? step : -step)));
  };

  const centering = jitter();
  const corners = jitter();
  const edges = jitter();
  const surface = jitter();

  const subs = { centering, corners, edges, surface };
  const lowest = Math.min(centering, corners, edges, surface);
  const avg = (centering + corners + edges + surface) / 4;
  // Graders weight the weakest sub-grade heavily.
  const grade = Math.max(1, Math.round((lowest * 0.6 + avg * 0.4) * 2) / 2);

  const defects: string[] = [];
  (Object.keys(subs) as (keyof typeof subs)[]).forEach((k) => {
    const v = subs[k];
    if (v >= 10) return;
    const pool = DEFECTS[k];
    const count = v >= 9 ? (Math.random() < 0.5 ? 1 : 0) : v >= 7 ? 1 : 2;
    for (let i = 0; i < count; i++) {
      const d = pick(pool);
      if (d && !defects.includes(d)) defects.push(d);
    }
  });

  const pristine = grade === 10 && lowest === 10;
  if (pristine) defects.length = 0;
  if (!pristine && defects.length === 0) defects.push("Minor factory imperfection under magnification");

  return { grade, label: labelFor(grade), centering, corners, edges, surface, defects, pristine };
}

export interface PulledCard {
  uid: string;
  card: OfficialCard;
  grade: CardGrade;
  /** Rarity × grade collector score, used for the daily top-pull board. */
  score: number;
}

export function scoreOf(rarity: string, grade: number) {
  return Math.round((RARITY_SCORE[rarity] ?? 1) * (0.5 + grade / 10) * 10);
}

/** Open one booster pack: 4 C + 2 U + 1 hit slot, ordered worst → best. */
export function openPack(): PulledCard[] {
  const picks: OfficialCard[] = [];
  for (let i = 0; i < 4; i++) picks.push(pickRarityCard("C", ["U", "R"]));
  for (let i = 0; i < 2; i++) picks.push(pickRarityCard("U", ["C", "R"]));
  const hitRarity = rollHitRarity();
  picks.push(pickRarityCard(hitRarity, ["RR", "R", "U"]));

  return picks.map((card) => {
    const grade = rollGrade();
    return {
      uid: `${card.code}-${Math.random().toString(36).slice(2, 10)}`,
      card,
      grade,
      score: scoreOf(card.rarity, grade.grade),
    };
  });
}

/** Odds table shown to users on the pack-opening page. */
export const PACK_ODDS_TABLE = [
  { slot: "Slots 1–4", detail: "Common (C)", odds: "Guaranteed" },
  { slot: "Slots 5–6", detail: "Uncommon (U)", odds: "Guaranteed" },
  ...HIT_SLOT_ODDS.map((o) => ({
    slot: "Slot 7 (hit)",
    detail: o.rarity,
    odds: `${(o.weight * 100).toFixed(o.weight < 0.01 ? 1 : 1)}%`,
  })),
];

// ---------------------------------------------------------------------------
// Openable products — the booster pack plus both Dawn of Palpagos trial decks.
// Each one costs a single daily pack.
// ---------------------------------------------------------------------------

import packFront from "@/assets/pack-front.png.asset.json";
import boosterBox from "@/assets/market/bp01-box.webp.asset.json";
import deckRedBlue from "@/assets/deck-red-blue.png.asset.json";
import deckGreenPurple from "@/assets/deck-green-purple.png.asset.json";

export type ProductId = "BP01" | "TD01" | "TD02" | "BOX01";

export interface PackProduct {
  id: ProductId;
  name: string;
  short: string;
  image: string;
  cards: number;
  blurb: string;
  /** Packs spent from the account balance to open this product. */
  cost: number;
}

/** How many cards a trial-deck opening reveals (a highlight sample of the 50-card deck). */
export const TRIAL_DECK_REVEAL = 7;

export const PACK_PRODUCTS: PackProduct[] = [
  {
    id: "BP01",
    name: 'Booster Pack "Dawn of Palpagos"',
    short: "Booster Pack",
    image: packFront.url,
    cards: CARDS_PER_PACK,
    cost: 1,
    blurb: "7 cards: 4 Common, 2 Uncommon, 1 hit slot with the real EBP01 pull rates.",
  },
  {
    id: "BOX01",
    name: 'Booster Box "Dawn of Palpagos" (12 packs · 84 cards)',
    short: "Booster Box",
    image: boosterBox.url,
    cards: CARDS_PER_PACK * PACKS_PER_BOX,
    cost: PACKS_PER_BOX,
    blurb: `The real EBP01 booster box: ${PACKS_PER_BOX} sealed boosters opened back to back, ${CARDS_PER_PACK * PACKS_PER_BOX} cards, ${PACKS_PER_BOX} hit slots. Costs ${PACKS_PER_BOX} packs.`,
  },
  {
    id: "TD01",
    name: 'Trial Deck "Dawn of Palpagos Red・Blue"',
    short: "Red・Blue Trial Deck",
    image: deckRedBlue.url,
    cards: TRIAL_DECK_REVEAL,
    cost: 1,
    blurb: "Aggressive Red・Blue starter — Grizzbolt, Blazamut and the ETD01 highlight cards.",
  },
  {
    id: "TD02",
    name: 'Trial Deck "Dawn of Palpagos Green・Purple"',
    short: "Green・Purple Trial Deck",
    image: deckGreenPurple.url,
    cards: TRIAL_DECK_REVEAL,
    cost: 1,
    blurb: "Tactical Green・Purple starter — Mossanda, Hoocrates and the ETD02 highlight cards.",
  },
];

/** Packs spent to open a product. */
export function productCost(id: ProductId): number {
  return PACK_PRODUCTS.find((p) => p.id === id)?.cost ?? 1;
}

/** Open a full booster box: PACKS_PER_BOX boosters, ordered worst → best. */
export function openBox(): PulledCard[] {
  const all: PulledCard[] = [];
  for (let i = 0; i < PACKS_PER_BOX; i++) all.push(...openPack());
  return all.sort((a, b) => a.score - b.score);
}

const TD_POOLS: Record<"TD01" | "TD02", OfficialCard[]> = {
  TD01: OFFICIAL_CARDS.filter((c) => c.set === "TD01" && !!c.image),
  TD02: OFFICIAL_CARDS.filter((c) => c.set === "TD02" && !!c.image),
};

/** Open a trial deck: a shuffled highlight sample, best card last. */
export function openTrialDeck(id: "TD01" | "TD02"): PulledCard[] {
  const pool = [...TD_POOLS[id]];
  const chosen: OfficialCard[] = [];
  while (chosen.length < TRIAL_DECK_REVEAL && pool.length > 0) {
    const i = Math.floor(Math.random() * pool.length);
    chosen.push(pool.splice(i, 1)[0]);
  }
  return chosen
    .map((card) => {
      const grade = rollGrade();
      return {
        uid: `${card.code}-${Math.random().toString(36).slice(2, 10)}`,
        card,
        grade,
        score: scoreOf(card.rarity, grade.grade),
      };
    })
    .sort((a, b) => a.score - b.score);
}

/** Open any product by id. */
export function openProduct(id: ProductId): PulledCard[] {
  if (id === "BP01") return openPack();
  if (id === "BOX01") return openBox();
  return openTrialDeck(id);
}
